import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../services/apiClient';
import { tidyAddress } from '../../utils/address';
import { LocationPickerModal } from './AdminBidDetail';
import SearchSelect from '../../components/SearchSelect';
import { PRICE_STRUCTURES, WORK_TYPES, classifyBid, priceStructureLabel, workTypeLabel } from '../../utils/bidTypes';
import { formatPhone } from '../../utils/phone';
import { TM_SOURCES } from './ExtraWorkCard';
import { ContactCheck } from '../../components/ContactCheck';

// ─────────────────────────────────────────────────────────────────────────────
// Job Information — who the job is for, who we answer to, and how it's priced.
//
// Every field comes from the bid, because that's where it was first written down
// and a job should never disagree with the bid it came from. But a bid is a moment
// and a job is a year: people leave, a GC changes, a sector gets corrected. So an
// Executive can edit any of it here and the edit goes back to the bid — one record,
// not a copy that quietly drifts out of step.
//
// Deliberately NOT here: the start and completion dates (the Big Picture schedule
// owns those now) and the bid's pricing detail — rates, margins, line items.
// ─────────────────────────────────────────────────────────────────────────────

// Work Type / Price Structure options live in utils/bidTypes.js (shared with the New
// Bid form). Pricing (Hard bid vs T&M) is its own question below.
const JOB_TYPES = ['Prime', 'Sub-Contractor'];
const SECTORS   = ['Public', 'Private'];
const PRICING   = [['hard', 'Hard Bid'], ['tm', 'Time & Material']];

export const lbl = { fontSize: 10, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 };
const val = { fontSize: 13.5, color: '#111', fontWeight: 600 };
export const inp = { border: '1px solid #d1d5db', borderRadius: 7, padding: '7px 9px', fontSize: 13, width: '100%', boxSizing: 'border-box', background: '#fff', color: '#111', fontFamily: 'inherit' };

const personName = u => `${u.firstName || u.first_name || ''} ${u.lastName || u.last_name || ''}`.trim();

export function Field({ label, note, children, span }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, gridColumn: span ? `span ${span}` : undefined }}>
      <div style={lbl}>{label}{note && <span style={{ color: '#d1d5db', fontWeight: 700 }}> · {note}</span>}</div>
      {children}
    </div>
  );
}

// Opened out, the card is a few boxed groups with the same column rhythm, so the
// fields line up under each other instead of flowing wherever the widths fall.
const card = { border: '1px solid #e5e7eb', borderRadius: 12, background: '#fafafa', padding: '14px 16px', minWidth: 0 };
const cardHead = { fontSize: 11, fontWeight: 800, color: '#c2410c', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 };
const cols = n => ({ display: 'grid', gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, gap: 14 });
// On a phone the three-across rows squeezed every box to a sliver (Ryan, Oct 21 2026: the job
// header on 2325-WO0002). Every grid here carries this class; under 640px wide it becomes one
// column and a box that spans two drops its span. Desktop is unchanged.
const NARROW_CSS = '@media (max-width: 640px) { .jh-grid { grid-template-columns: minmax(0, 1fr) !important; } .jh-grid > * { grid-column: auto !important; } }';
function Section({ title, children, cols: n = 3 }) {
  return (
    <div style={card}>
      <div style={cardHead}>{title}</div>
      <div className="jh-grid" style={cols(n)}>{children}</div>
    </div>
  );
}
// A dropdown that also accepts what you type: the current value is always in the
// list, so a company entered by hand still reads back as the chosen one.
function pickOptions(current, options) {
  const has = options.some(o => o.label.trim().toLowerCase() === String(current || '').trim().toLowerCase());
  return current && !has ? [{ value: '__current', label: current }, ...options] : options;
}
const valueOf = (current, options) => {
  const hit = options.find(o => o.label.trim().toLowerCase() === String(current || '').trim().toLowerCase());
  return hit ? hit.value : (current ? '__current' : '');
};

// The same card also heads the bid page's Job Description tile, before there is a
// project. There it saves through the bid page's own autosave (`saveBid` /
// `savePricing`), hides the Owner box (the bid has a whole owner-contact card for
// that), and takes the bid-only facts and fields — bid date & time, pre-bid meeting,
// certified payroll, T&M price — as `extraFacts` / `extraChips` / `extraFields`.
export default function JobHeaderPillow({ project, bid, canEdit = false, compact = false, onChanged,
  saveBid = null, savePricing = null, hideOwner = false, extraFacts = [], extraChips = [], extraFields = null }) {
  const navigate = useNavigate();
  // A T&M job's price structure (Job Rate Sheet / Standard T&M / Loops / Common Items /
  // Pole Hold / Drilling). Picking one loads its lines onto the T&M Rates once and
  // makes it lead on the Project T&M Items tile and the card.
  const [tmNote, setTmNote] = useState('');
  // A work order that grew into a real job (2638, Sep 24 2026) — or the other way.
  async function saveKind(kind) {
    if (!project?.id) return;
    const word = kind === 'job' ? 'a job (Projects list, Big Picture, Large Job Billing)' : 'a work order (Small Jobs board, Work Order Billing)';
    if (!window.confirm(`Make ${project.project_number || 'this'} ${word}? Everything on it stays — the bid, cards, POs, documents and T&M — only where it lives changes.`)) return;
    setSaving('kind'); setErr('');
    try { await apiFetch(`/projects/${project.id}/kind`, { method: 'PATCH', body: JSON.stringify({ kind }) }); onChanged && onChanged(); }
    catch (e) { setErr(e?.error || e?.message || 'That did not save.'); }
    setSaving('');
  }
  async function saveTmStructure(v) {
    if (!project?.id) return;
    setSaving('tm_structure'); setErr(''); setTmNote('');
    try {
      const r = await apiFetch(`/projects/${project.id}/tm-rate-source`, { method: 'PUT', body: JSON.stringify({ source: v || null }) });
      window.dispatchEvent(new CustomEvent('titan:tm-rate-source-changed', { detail: { projectId: project.id, source: v || null } }));
      window.dispatchEvent(new CustomEvent('titan:tm-items-changed'));
      const n = (r.added || []).length, np = (r.need_price || []).length;
      setTmNote(n ? `Put ${n} line${n === 1 ? '' : 's'} on the T&M Rates${np ? ` — ${np} still need${np === 1 ? 's' : ''} a price` : ''}.` : (v ? 'Nothing new to add — the lines were already there.' : ''));
      onChanged && onChanged();
    } catch (e) { setErr(e?.error || e?.message || 'That did not save.'); }
    setSaving('');
  }
  const [pms, setPms] = useState([]);
  const [saving, setSaving] = useState('');
  const [err, setErr] = useState('');
  // The Titan admin person (Pat, Oct 9 2026) — Jessica, Nallely or Erin, changeable any time.
  const [admins, setAdmins] = useState([]);
  const [adminId, setAdminId] = useState(project?.titan_admin_id || '');
  useEffect(() => { setAdminId(project?.titan_admin_id || ''); }, [project?.id, project?.titan_admin_id]);
  useEffect(() => { if (project?.id) apiFetch('/titan-admins').then(r => setAdmins(Array.isArray(r) ? r : [])).catch(() => {}); }, [project?.id]);
  async function saveAdmin(v) {
    setSaving('admin'); setErr('');
    try { const r = await apiFetch(`/projects/${project.id}/titan-admin`, { method: 'PUT', body: JSON.stringify({ user_id: v || null }) }); setAdminId(r.titan_admin_id || ''); onChanged && onChanged(); }
    catch (e) { setErr(e?.error || e?.message || 'That did not save.'); }
    setSaving('');
  }
  const adminName = (admins.find(a => a.id === adminId) || {}).name || project?.titan_admin_name || null;
  const adminField = project?.id ? (canEdit
    ? <select value={adminId} disabled={saving === 'admin'} onChange={e => saveAdmin(e.target.value)} onClick={e => e.stopPropagation()}
        title="Jessica, Nallely or Erin — Nallely has the work orders and even-numbered jobs, Jessica the odd ones, unless you pick"
        style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '5px 8px', fontSize: 13.5, fontWeight: 700, background: '#fff', cursor: 'pointer', maxWidth: '100%' }}>
        {!adminId && <option value="">—</option>}
        {admins.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
    : (adminName || '—')) : null;
  const [draft, setDraft] = useState({});          // typed but not yet saved
  const [pickMap, setPickMap] = useState(false);
  const [editing, setEditing] = useState(false);   // opened out, it still reads first
  // Who we could be working for: the GCs this bid was quoted to, then the whole
  // client directory — so the General Contractor is picked, not retyped.
  const [recipients, setRecipients] = useState([]);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);    // the chosen GC's people on file
  // Invoices usually go to the person we deal with. A separate billing contact is the
  // exception, so it's a box to tick — the fields only appear once it is.
  const [billingDifferent, setBillingDifferent] = useState(false);

  useEffect(() => {
    if (!canEdit) return;
    apiFetch('/users')
      .then(r => setPms((Array.isArray(r) ? r : []).filter(u => /ProjectManager|Admin|Executive/.test(u.role || ''))))
      .catch(() => {});
  }, [canEdit]);
  useEffect(() => {
    if (!editing || !project) return;
    apiFetch('/clients').then(r => setClients(Array.isArray(r) ? r : [])).catch(() => {});
    if (bid?.id) apiFetch(`/bids/${bid.id}/recipients`).then(r => setRecipients(Array.isArray(r) ? r : [])).catch(() => {});
  }, [editing, project, bid?.id]);
  const gcCompany = project?.awarded_by_company || '';
  const gcClient = clients.find(c => c.name.trim().toLowerCase() === gcCompany.trim().toLowerCase()) || null;
  useEffect(() => {
    if (!editing || !gcClient) { setContacts([]); return; }
    apiFetch(`/clients/${gcClient.id}/contacts`).then(r => setContacts(Array.isArray(r) ? r : [])).catch(() => setContacts([]));
  }, [editing, gcClient?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setBillingDifferent(!!(project?.billing_contact_company || project?.billing_contact_name || project?.billing_contact_email || project?.billing_contact_phone));
  }, [project?.id, project?.billing_contact_company, project?.billing_contact_name, project?.billing_contact_email, project?.billing_contact_phone]);

  const pmName = id => {
    const u = pms.find(x => String(x.id) === String(id));
    return u ? personName(u) : null;
  };

  // One screen of fields, three places they actually live.
  async function save(where, body) {
    setSaving(where); setErr('');
    try {
      if (where === 'pricing' && savePricing) await savePricing(body.pricing_type);
      else if (where === 'bid' && saveBid)    await saveBid(body);
      else if (where === 'pricing')  await apiFetch(`/bids/${bid.id}/pricing`, { method: 'PATCH', body: JSON.stringify(body) });
      // The GC's contact person is the Contract pillow's 'GC / Owner Contact' — the
      // same field, so it goes through the same route rather than growing a second
      // one that would disagree with it.
      else if (where === 'contract') await apiFetch(`/projects/${project.id}/contract`, { method: 'PATCH', body: JSON.stringify(body) });
      else if (where === 'project')  await apiFetch(`/projects/${project.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      else                           await apiFetch(`/bids/${bid.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      if (onChanged) await onChanged();
    } catch (e) {
      setErr(e?.error || 'Could not save that.');
      // A renumber onto another job's number: say so out loud, not only in small red text.
      if (e?.code === 'number_taken') { window.alert(e.error); setDraft(d => { const { project_number, ...rest } = d; return rest; }); }
    }
    setSaving('');
  }

  const shown = (k, fallback) => (k in draft ? draft[k] : fallback);

  if (!bid) {
    return <div style={{ fontSize: 13, color: '#9ca3af' }}>This job has no bid behind it, so there's nothing to import.</div>;
  }

  // A card to read, not a form to fill in — in the pillow's window, for anyone who
  // can't edit, and opened out until Edit is pressed. Job facts get looked at far
  // more often than they get changed, and a screen of input boxes reads as a chore
  // and invites a stray keystroke.
  if (compact || !canEdit || !editing) {
    return (
      <Summary project={project} bid={bid} pmName={pmName} wide={!compact} extraFacts={extraFacts} extraChips={extraChips} adminField={adminField}
        onEdit={canEdit && !compact ? () => setEditing(true) : null}
        onOpenBid={canEdit && bid?.id ? () => navigate(`/admin/bids/${bid.id}`) : null} />
    );
  }

  // ── General Contractor / General Contact pickers ──────────────────────────
  // Recipients first (the GCs this bid went to — one of them is who awarded it),
  // then everyone in the client directory. Picking fills company, contact, email
  // and phone in one go; typing a name that isn't on file still works.
  const seen = new Set();
  const gcOptions = [
    ...recipients.filter(r => r.client_name && !seen.has(r.client_name.toLowerCase()) && seen.add(r.client_name.toLowerCase()))
      .map(r => ({ value: `rec:${r.id}`, label: r.client_name, sub: r.contact_name ? `${r.contact_name} · quoted` : 'quoted' })),
    ...clients.filter(c => !seen.has(c.name.toLowerCase()) && seen.add(c.name.toLowerCase()))
      .map(c => ({ value: `cli:${c.id}`, label: c.name, sub: c.contact || undefined })),
  ];
  const gcName = shown('awarded_by_company', gcCompany);
  const gcList = pickOptions(gcName, gcOptions);
  function pickGc(v) {
    if (v === '__current') return;
    if (v.startsWith('rec:')) {
      const r = recipients.find(x => `rec:${x.id}` === v);
      if (!r) return;
      // Drop the typed drafts for these — the saved record is the truth once it lands.
      setDraft(d => { const { awarded_by_company, awarded_by_name, awarded_by_email, awarded_by_phone, ...rest } = d; return rest; });
      return save('project', { awardedByClientId: r.client_id || undefined, awardedByCompany: r.client_name,
        awardedByName: r.contact_name || r.client_name, awardedByEmail: r.contact_email || null, awardedByPhone: r.contact_phone || null });
    }
    const c = clients.find(x => `cli:${x.id}` === v);
    if (!c) return;
    setDraft(d => { const { awarded_by_company, awarded_by_name, awarded_by_email, awarded_by_phone, ...rest } = d; return rest; });
    // The server fills the company + mailing address from the client record.
    return save('project', { awardedByClientId: c.id, awardedByContact: c.contact || null, awardedByName: c.contact || c.name, awardedByPhone: c.phone || null, awardedByEmail: null });
  }
  function typeGc(text) {
    const v = String(text || '').trim();
    setDraft(d => ({ ...d, awarded_by_company: v }));
    save('project', { awardedByCompany: v || null, awardedByName: shown('awarded_by_name', project?.awarded_by_name || '') || null });
  }
  const seenC = new Set();
  const contactOptions = [
    ...recipients.filter(r => r.contact_name && (!gcName || (r.client_name || '').toLowerCase() === gcName.toLowerCase()))
      .filter(r => !seenC.has(r.contact_name.toLowerCase()) && seenC.add(r.contact_name.toLowerCase()))
      .map(r => ({ value: `rec:${r.id}`, label: r.contact_name, sub: [r.contact_email, formatPhone(r.contact_phone)].filter(Boolean).join(' · ') || 'quoted' })),
    ...contacts.filter(c => !seenC.has(c.name.toLowerCase()) && seenC.add(c.name.toLowerCase()))
      .map(c => ({ value: `con:${c.id}`, label: c.name, sub: [c.title, c.email, formatPhone(c.phone)].filter(Boolean).join(' · ') || undefined })),
  ];
  const contactName = shown('awarded_by_name', project?.awarded_by_name || '');
  const contactList = pickOptions(contactName, contactOptions);
  function pickContact(v) {
    if (v === '__current') return;
    const r = v.startsWith('rec:') ? recipients.find(x => `rec:${x.id}` === v) : null;
    const c = v.startsWith('con:') ? contacts.find(x => `con:${x.id}` === v) : null;
    const name = r?.contact_name || c?.name; if (!name) return;
    setDraft(d => { const { awarded_by_name, awarded_by_email, awarded_by_phone, ...rest } = d; return rest; });
    save('project', { awardedByName: name, awardedByCompany: gcName || null,
      awardedByEmail: (r?.contact_email || c?.email) || null, awardedByPhone: (r?.contact_phone || c?.phone) || null });
  }
  function typeContact(text) {
    const v = String(text || '').trim();
    setDraft(d => ({ ...d, awarded_by_name: v }));
    save('project', { awardedByName: v || null, awardedByCompany: gcName || null });
  }
  function setBilling(on) {
    setBillingDifferent(on);
    // Unticking clears the four fields — the invoices go back to the General Contact.
    if (!on) save('project', { billingContactCompany: '', billingContactName: '', billingContactEmail: '', billingContactPhone: '' });
  }
  const pickInp = { ...inp, padding: '7px 9px' };
  const bField = (key, label, placeholder) => (
    <Field label={label} key={key}>
      <input style={inp} value={shown(key, project?.[key] || '')} placeholder={placeholder}
        onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
        onBlur={e => { const v = key === 'billing_contact_phone' ? formatPhone(e.target.value) : e.target.value; if (v !== (project?.[key] || '')) save('project', { [{
          billing_contact_company: 'billingContactCompany', billing_contact_name: 'billingContactName',
          billing_contact_email: 'billingContactEmail', billing_contact_phone: 'billingContactPhone' }[key]]: v }); }} />
    </Field>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <style>{NARROW_CSS}</style>
      {/* Opened out, the same two things still lead — the number as it is, because
          changing it moves the job's time-clock jobcode with it, and the name
          editable because it's just what the job is called. */}
      <div className="jh-grid" style={{ ...card, display: 'grid', gridTemplateColumns: 'minmax(120px, auto) minmax(0, 1.4fr) minmax(0, 1fr)', gap: 14, alignItems: 'end' }}>
        <div>
          <div style={lbl}>{project ? 'Job number' : 'Status'}</div>
          {/* The number is editable here — it used to be set once at award and then
              fixed, so 'WO26125VD - Job #16919' had no way to become 'WO26125VD'
              (Pat, Sep 2026). Everything keyed on it (the T&M time-clock match, document
              titles, the boards) reads it live, so a change follows through. */}
          {project ? (
            <input style={{ ...inp, fontSize: 22, fontWeight: 800, color: '#c2410c', fontFamily: 'monospace', padding: '4px 8px' }}
              value={shown('project_number', project.project_number || '')}
              placeholder="e.g. 2648 or WO26125"
              onChange={e => setDraft(d => ({ ...d, project_number: e.target.value }))}
              onBlur={e => { const v = e.target.value.trim();
                if (!v || v === (project.project_number || '')) return;
                save('project', { projectNumber: v }); }} />
          ) : (
            <div style={{ fontSize: 26, fontWeight: 800, color: '#c2410c', lineHeight: 1.1 }}>BID</div>
          )}
        </div>
        <Field label="Job name">
          <input style={{ ...inp, fontSize: 15, fontWeight: 700 }}
            value={shown('job_name', project?.name || bid.job_name || '')}
            placeholder="What this job is called"
            onChange={e => setDraft(d => ({ ...d, job_name: e.target.value }))}
            onBlur={e => { const v = e.target.value.trim();
              if (!v) return;
              if (!project) { if (v !== (bid.job_name || '')) save('bid', { job_name: v }); }
              else if (v !== (project.name || '')) save('project', { name: v, job_name: v }); }} />
        </Field>
        {/* Where the job is. The same picker the bid uses, and picking on it saves
            the point to the project's location as well, so the job appears on the
            field map rather than only reading as an address. */}
        <Field label="Location">
          <div style={{ display: 'flex', gap: 5 }}>
            <input style={inp} value={shown('job_location', bid.job_location || '')}
              placeholder="Address, or drop a pin"
              onChange={e => setDraft(d => ({ ...d, job_location: e.target.value }))}
              onBlur={e => { if (e.target.value !== (bid.job_location || '')) save('bid', { job_location: e.target.value }); }} />
            <button type="button" onClick={() => setPickMap(true)} title="Drop a pin on the map"
              style={{ ...inp, width: 'auto', whiteSpace: 'nowrap', cursor: 'pointer', fontWeight: 700, color: '#1d4ed8' }}>
              📍 Map
            </button>
          </div>
        </Field>
      </div>

      {/* What we're being asked to perform — typed on the New Bid form, and until now
          never shown again (Ryan, Sep 30 2026). It is the bid's scope: it becomes the
          job's description at award, the Scope box on the work order card and the
          Scope of Work on the invoice, so nobody has to retype it. A job made without
          a bid keeps its description on the project. */}
      <div style={card}>
        <Field label="Job Description" note="what we're being asked to perform">
          <textarea style={{ ...inp, resize: 'vertical', minHeight: 64 }} rows={3}
            value={shown('scope', (bid.scope ?? project?.description) || '')}
            placeholder="Describe the work — this becomes the Scope on the work order card and the invoice"
            onChange={e => setDraft(d => ({ ...d, scope: e.target.value }))}
            onBlur={e => { const v = e.target.value;
              if (bid.id && v !== (bid.scope || '')) save('bid', { scope: v });
              else if (!bid.id && project?.id && v !== (project.description || '')) save('project', { description: v }); }} />
        </Field>
      </div>

      {/* Who the job is for and who we answer to. GC company + contact are assigned at
          award time and live on the project, so they only appear once there is one —
          a bid is addressed via Bid Recipients. Blank GC means we're the prime, and
          that decides who change orders and submittals are addressed to. */}
      {(project || !hideOwner) && (
      <Section title="Who's involved" cols={3}>
        {!hideOwner && (
        <Field label="Owner" note="from the bid">
          <input style={inp} value={shown('client_name', bid.client_name || '')} placeholder="Who the job is for"
            onChange={e => setDraft(d => ({ ...d, client_name: e.target.value }))}
            onBlur={e => { if (e.target.value !== (bid.client_name || '')) save('bid', { client_name: e.target.value }); }} />
        </Field>
        )}
        {project && (<>
        <Field label="General Contractor" note="pick, or type">
          <SearchSelect value={valueOf(gcName, gcList)} options={gcList} onChange={pickGc} onCreate={typeGc}
            placeholder={`Blank = we're prime (${bid.client_name || 'the Owner'})`} inputStyle={pickInp} />
        </Field>
        {/* This is who everything is addressed to. The bid may have gone to someone
            else at that company — an estimator, whoever picked it up — but once the
            job is running, correspondence follows this name, not the bid's. */}
        <Field label="General Contact" note="all correspondence">
          <SearchSelect value={valueOf(contactName, contactList)} options={contactList} onChange={pickContact} onCreate={typeContact}
            placeholder="Who we deal with there" inputStyle={pickInp} />
        </Field>
        <Field label="Contact email">
          <input style={inp} value={shown('awarded_by_email', project?.awarded_by_email || '')} placeholder="Email"
            onChange={e => setDraft(d => ({ ...d, awarded_by_email: e.target.value }))}
            onBlur={e => { if (e.target.value !== (project?.awarded_by_email || '')) save('project', { awardedByEmail: e.target.value || null }); }} />
        </Field>
        <Field label="Contact phone">
          <input style={inp} value={shown('awarded_by_phone', project?.awarded_by_phone || '')} placeholder="Phone"
            onChange={e => setDraft(d => ({ ...d, awarded_by_phone: e.target.value }))}
            onBlur={e => { const v = formatPhone(e.target.value); if (v !== (project?.awarded_by_phone || '')) save('project', { awardedByPhone: v || null }); }} />
        </Field>
        {/* Is the General Contact in the Customer List? Offer to add them (Pat, Oct 6 2026). */}
        <div style={{ gridColumn: '1 / -1' }}>
          <ContactCheck name={project?.awarded_by_name} email={project?.awarded_by_email} phone={project?.awarded_by_phone} clientName={gcName || bid.client_name}
            onUse={c => save('project', { awardedByName: c.name, awardedByCompany: c.client_name || gcName || null, awardedByEmail: c.email || project?.awarded_by_email || null, awardedByPhone: c.phone || project?.awarded_by_phone || null })} />
        </div>
        {/* Who the invoices go to. Usually the General Contact; when it's the GC's AP
            desk instead, tick the box and say who. An invoice's Bill To starts here. */}
        <Field label="Billing" note="invoices">
          <label style={{ ...inp, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, color: billingDifferent ? '#111' : '#6b7280' }}>
            <input type="checkbox" checked={billingDifferent} onChange={e => setBilling(e.target.checked)} style={{ margin: 0 }} />
            Different from the General Contact
          </label>
        </Field>
        {billingDifferent && (<>
          {bField('billing_contact_company', 'Billing company', 'Company (blank = the GC)')}
          {bField('billing_contact_name', 'Billing contact', 'Name')}
          {bField('billing_contact_email', 'Billing email', 'Email')}
          {bField('billing_contact_phone', 'Billing phone', 'Phone')}
          <div style={{ gridColumn: '1 / -1' }}>
            <ContactCheck name={project?.billing_contact_name} email={project?.billing_contact_email} phone={project?.billing_contact_phone} clientName={project?.billing_contact_company || gcName || bid.client_name}
              onUse={c => save('project', { billingContactName: c.name, billingContactCompany: project?.billing_contact_company || c.client_name || '', billingContactEmail: c.email || project?.billing_contact_email || '', billingContactPhone: c.phone || project?.billing_contact_phone || '' })} />
          </div>
        </>)}
        </>)}
      </Section>
      )}

      {/* Three questions in Ryan's order: how it's bid (Hard Bid vs T&M — this one
          changes how the job is invoiced, not just a label), how the number is built
          (Lump Sum / Unit Price — a T&M bid's structure is its rate lines, so hidden
          there), and what kind of work it is (drives the quote layout and, at award,
          the workflow). The server keeps the legacy bid_type in step with all three. */}
      <Section title="How it's set up" cols={3}>
        <Field label="Project Manager">
          <select style={{ ...inp, cursor: 'pointer' }} value={project?.pm_id || bid?.bid_pm_id || ''}
            onChange={e => save('bid', { bid_pm_id: e.target.value || null })}>
            <option value="">Unassigned</option>
            {pms.map(u => <option key={u.id} value={u.id}>{personName(u)}</option>)}
          </select>
        </Field>
        <Field label="Bid Type" note="drives T&M billing">
          <select style={{ ...inp, cursor: 'pointer' }} value={bid.pricing_type || ''}
            onChange={e => { if (e.target.value) save('pricing', { pricing_type: e.target.value }); }}>
            <option value="">—</option>
            {PRICING.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </Field>
        {bid.pricing_type !== 'tm' ? (
          <Field label="Price Structure">
            <select style={{ ...inp, cursor: 'pointer' }} value={classifyBid(bid).price_structure || 'lump_sum'}
              onChange={e => save('bid', { price_structure: e.target.value })}>
              {PRICE_STRUCTURES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          </Field>
        ) : project?.id ? (
          <Field label="Price Structure" note="leads on the T&M tile and card">
            <select style={{ ...inp, cursor: 'pointer' }} value={project?.tm_rate_source || ''} disabled={saving === 'tm_structure'}
              onChange={e => saveTmStructure(e.target.value)}>
              <option value="">— pick one —</option>
              {TM_SOURCES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
            {tmNote && <div style={{ fontSize: 11.5, color: '#0f766e', marginTop: 4 }}>{tmNote}</div>}
          </Field>
        ) : (
          <Field label="Price Structure">
            <div style={{ ...inp, background: '#f3f4f6', color: '#6b7280', fontWeight: 600 }}>Rate lines (T&M) — picked on the job once awarded</div>
          </Field>
        )}
        <Field label="Work Type" note="drives the workflow">
          <select style={{ ...inp, cursor: 'pointer' }} value={classifyBid(bid).work_type || 'general'}
            onChange={e => save('bid', { work_type: e.target.value })}>
            {WORK_TYPES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </Field>
        <Field label="Job Type">
          <select style={{ ...inp, cursor: 'pointer' }} value={bid.job_type || ''}
            onChange={e => save('bid', { job_type: e.target.value || null })}>
            <option value="">—</option>
            {JOB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        {project?.id && (
          <Field label="Job kind" note="job or work order">
            <select style={{ ...inp, cursor: 'pointer' }} value={project.division === 'small' ? 'work_order' : 'job'} disabled={saving === 'kind'}
              onChange={e => saveKind(e.target.value)}>
              <option value="job">Job — Projects list, Big Picture, Large Job Billing</option>
              <option value="work_order">Work Order — Small Jobs board, Work Order Billing</option>
            </select>
          </Field>
        )}
        <Field label="Public or Private">
          <select style={{ ...inp, cursor: 'pointer' }} value={bid.job_sector || ''}
            onChange={e => save('bid', { job_sector: e.target.value || null })}>
            <option value="">—</option>
            {SECTORS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </Section>

      {extraFields && (
        <Section title="Bid details" cols={3}>{extraFields}</Section>
      )}

      {pickMap && (
        <LocationPickerModal value={bid.job_location}
          onConfirm={async ({ address, lat, lng }) => {
            setDraft(d => ({ ...d, job_location: address }));
            await save('bid', { job_location: address });
            // The dropped pin is what puts the job on the field map.
            if (project?.id && address) {
              apiFetch(`/projects/${project.id}/locations`, {
                method: 'POST',
                body: JSON.stringify({ address, lat: lat ?? null, lng: lng ?? null, replace: true }),
              }).catch(() => {});
            }
          }}
          onClose={() => setPickMap(false)} />
      )}

      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={() => setEditing(false)}
          style={{ padding: '8px 18px', borderRadius: 9, border: 'none', background: '#1e3a5f', color: '#fff',
            fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
          Done
        </button>
        {/* Every field saves as you leave it, so Done closes editing rather than
            committing anything — saying so beats implying a save that already happened. */}
        <span style={{ fontSize: 11.5, fontWeight: err ? 700 : 400, color: err ? '#b91c1c' : '#9ca3af' }}>
          {err || (saving ? 'Saving…' : 'Changes save as you go. These come from the bid, so editing here changes the bid too.')}
        </span>
      </div>
    </div>
  );
}

// ── The card as it reads ─────────────────────────────────────────────────────
const chip = tint => ({
  fontSize: 10.5, fontWeight: 800, padding: '3px 9px', borderRadius: 999,
  border: `1px solid ${tint}33`, background: `${tint}14`, color: tint, whiteSpace: 'nowrap',
});

function Line({ label, children }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ ...lbl, marginBottom: 1 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#111', fontWeight: 600, lineHeight: 1.35, wordBreak: 'break-word' }}>{children}</div>
    </div>
  );
}

function Summary({ project, bid, pmName, wide = false, onEdit, onOpenBid = null, extraFacts = [], extraChips = [], adminField = null }) {
  const gc = project?.awarded_by_company || project?.awarded_by_name;
  // The New Bid form's "Job Description" — the bid's scope, which the job's
  // description, the work order card and the invoice all start from.
  const scope = String((bid?.scope ?? project?.description) || '').trim();
  const contact = project?.awarded_by_name && project.awarded_by_name !== gc ? project.awarded_by_name : null;
  const cls = classifyBid(bid);
  const chips = [
    [bid?.pricing_type === 'tm' ? 'Time & Material' : bid?.pricing_type === 'hard' ? 'Hard Bid' : null, '#7c3aed'],
    [bid?.pricing_type === 'tm' ? null : priceStructureLabel(cls.price_structure), '#0369a1'],
    [workTypeLabel(cls.work_type), '#1e3a5f'],
    [bid?.job_type, '#0f766e'],
    [bid?.job_sector, '#a16207'],
    ...extraChips,
  ].filter(([t]) => t);

  // A pillow's window is about 220px of usable height, so this is ordered by what
  // has to survive being cut off: the number and name, then how the job is priced
  // and sold, then who it involves. The address gets one line and an ellipsis —
  // knowing roughly where beats losing the rest of the card to a long address.
  // Opened out there is room for all of it; in the window only the few that matter.
  // A GC is assigned at award, so on a bid there's none to show — the bid page passes
  // who it's bidding to in extraFacts instead.
  const contactLine = project?.awarded_by_name
    ? [project.awarded_by_name, project.awarded_by_email, formatPhone(project.awarded_by_phone)].filter(Boolean).join(' · ')
    : null;
  const billingLine = project
    ? ([project.billing_contact_name, project.billing_contact_company, project.billing_contact_email, formatPhone(project.billing_contact_phone)].filter(Boolean).join(' · ') || (wide ? 'Same as General Contact' : null))
    : null;
  // Two groups, read the way the edit form is laid out: who is involved, then how
  // the job is set up. Bid-only facts (bid date, pre-bid…) join the second group.
  // On a bid there's no GC yet, so the Owner joins the other group rather than
  // sitting alone in a card of its own.
  const whoFacts = project ? [
    ['Owner', bid?.client_name],
    ['General Contractor', gc || 'Titan is the prime'],
    ['General Contact', (contact ? contactLine : null) || (gc ? '—' : null)],
    ['Billing Contact', billingLine],
  ].filter(([, v]) => v !== null) : [];
  const howFacts = [
    ...(project ? [] : [['Owner', bid?.client_name]]),
    ['Project Manager', pmName(project?.pm_id || bid?.bid_pm_id) || project?.pm_name || bid?.bid_pm_name],
    // Ryan's order: how it's bid, how the number is built, what kind of work.
    ['Bid Type', bid?.pricing_type === 'tm' ? 'Time & Material' : bid?.pricing_type === 'hard' ? 'Hard Bid' : null],
    ['Price Structure', bid?.pricing_type === 'tm' ? 'Rate lines (T&M)' : priceStructureLabel(cls.price_structure)],
    ['Work Type', workTypeLabel(cls.work_type)],
    ['Job Type', bid?.job_type],
    ['Public or Private', bid?.job_sector],
    ...extraFacts,
    ...(project?.id ? [['Titan Admin Person', adminField]] : []),
  ].filter(([, v]) => v !== null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: wide ? 14 : 9 }}>
      <style>{NARROW_CSS}</style>
      <div style={{ ...(wide ? card : {}), display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: wide ? 28 : 22, fontWeight: 800, color: '#c2410c', lineHeight: 1.1 }}>
          {project ? (project.project_number || '—') : 'BID'}
        </div>
        <div style={{ fontSize: wide ? 16 : 14, fontWeight: 700, color: '#111', marginTop: 2, lineHeight: 1.3 }}>
          {project?.name || bid?.job_name || 'Unnamed job'}
        </div>
        {bid?.job_location && (
          <div title={bid.job_location}
            style={{ fontSize: 12, color: '#6b7280', marginTop: 3,
              whiteSpace: wide ? 'normal' : 'nowrap',
              overflow: 'hidden', textOverflow: 'ellipsis' }}>📍 {tidyAddress(bid.job_location)}</div>
        )}
        {/* In the window, three lines of the description and an ellipsis; opened out it
            gets a card of its own below. */}
        {!wide && scope && (
          <div title={scope}
            style={{ fontSize: 12.5, color: '#374151', marginTop: 6, lineHeight: 1.4, whiteSpace: 'pre-wrap',
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{scope}</div>
        )}
        {wide && chips.length > 0 && (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 10 }}>
            {chips.map(([text, tint]) => <span key={text} style={chip(tint)}>{text}</span>)}
          </div>
        )}
        </div>
        {onEdit && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', flexShrink: 0 }}>
            {onEdit && (
              <button onClick={onEdit}
                style={{ padding: '7px 16px', borderRadius: 999, border: '1px solid #fed7aa', background: '#fff7ed',
                  color: '#c2410c', fontSize: 12.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                ✎ Edit
              </button>
            )}
          </div>
        )}
      </div>

      {!wide && chips.length > 0 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {chips.map(([text, tint]) => <span key={text} style={chip(tint)}>{text}</span>)}
        </div>
      )}

      {!wide && <div style={{ height: 1, background: '#f1f1f1' }} />}

      {wide && (
        <div style={card}>
          <div style={cardHead}>Job Description · what we're being asked to perform</div>
          {scope
            ? <div style={{ fontSize: 13.5, color: '#111', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{scope}</div>
            : <div style={{ fontSize: 13, color: '#9ca3af' }}>Nothing written yet{onEdit ? ' — press ✎ Edit to describe the work.' : '.'}</div>}
        </div>
      )}

      {wide ? (
        <div className="jh-grid" style={{ display: 'grid', gridTemplateColumns: whoFacts.length ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)', gap: 14, alignItems: 'stretch' }}>
          {whoFacts.length > 0 && (
            <div style={card}>
              <div style={cardHead}>Who's involved</div>
              <div className="jh-grid" style={{ ...cols(2), rowGap: 16 }}>
                {whoFacts.map(([k, v]) => <Line key={k} label={k}>{v || '—'}</Line>)}
              </div>
            </div>
          )}
          <div style={card}>
            <div style={cardHead}>How it's set up</div>
            <div className="jh-grid" style={{ ...cols(2), rowGap: 16 }}>
              {howFacts.map(([k, v]) => <Line key={k} label={k}>{v || '—'}</Line>)}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 9 }}>
          <Line label="Owner">{bid?.client_name || '—'}</Line>
          <Line label="General Contractor">
            {gc || <span style={{ fontWeight: 500, color: '#6b7280' }}>Titan is the prime</span>}
            {contact && <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>{contact}</div>}
          </Line>
          <Line label="Project Manager">{pmName(project?.pm_id || bid?.bid_pm_id) || project?.pm_name || bid?.bid_pm_name || '—'}</Line>
          {project?.id && <Line label="Titan Admin Person">{adminField}</Line>}
        </div>
      )}

      {/* The one thing the Bid & Estimate pillow was still needed for (Pat, Sep 2026) — a quiet
          link in the body of the Job header tile instead of a button squeezing the job number
          beside it (Pat, Oct 6 2026: "more discreet"). */}
      {onOpenBid && (
        <button onClick={onOpenBid}
          style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: '2px 0', color: '#6b7280',
            fontSize: 12.5, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}>
          Open the bid page ›
        </button>
      )}
    </div>
  );
}
